import { API_CONFIG, ApiError } from '@/lib/api';
import { withResolvedMimeType, type AttachmentKind } from '@/lib/inbox/attachmentRules';

/** What a team chat attachment message carries in its `metadata`. */
export interface TeamChatAttachmentMeta {
  url: string;
  key: string;
  originalName: string;
  mimeType: string;
  size: number;
}

export interface TeamChatAttachmentMessage {
  content: string;
  messageType: AttachmentKind;
  metadata: TeamChatAttachmentMeta;
}

const KIND_ICON: Record<AttachmentKind, string> = {
  image: '🖼️',
  video: '🎬',
  audio: '🎵',
  document: '📎',
};

/**
 * Upload one file for team chat and describe the message that carries it.
 * The browser asks the API for a presigned S3 link, then sends the file
 * straight to S3 - not through the API, whose request body is capped at about
 * 6 MB on Lambda. The API checks the type and size first (see
 * backend/src/domain/chat/teamChatAttachments.js).
 */
export async function uploadTeamChatAttachment(input: File, kind: AttachmentKind): Promise<TeamChatAttachmentMessage> {
  const file = withResolvedMimeType(input);
  const token = localStorage.getItem('auth_token');
  if (!token) throw new Error('Not authenticated');

  const presignRes = await fetch(`${API_CONFIG.BASE_URL}/chat/upload/presign`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fileName: file.name, fileType: file.type || 'application/octet-stream', size: file.size }),
  });
  const presign = await presignRes.json().catch(() => ({}));
  if (!presignRes.ok || presign?.success === false) {
    throw ApiError.fromResult(presign, 'Failed to upload attachment');
  }
  const { presignedUrl, url, key, contentType } = presign.data || {};

  const putRes = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Upload failed (HTTP ${putRes.status}). Please try again.`);
  }

  // Add it to the Media library. Not waited on: if this fails, the file is
  // still sent - it is only missing from the library.
  void fetch(`${API_CONFIG.BASE_URL}/chat/upload/complete`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, originalName: file.name, mimeType: file.type || contentType, size: file.size }),
  }).catch(() => {});

  return {
    content: `${KIND_ICON[kind]} ${file.name}`,
    messageType: kind,
    metadata: {
      url,
      key,
      originalName: file.name,
      mimeType: file.type,
      size: file.size,
    },
  };
}

/**
 * The kind a stored message shows as. Older messages used `file` for every
 * non-image upload; anything with a link and no known kind is a document.
 */
export function teamChatAttachmentKind(messageType: string | null | undefined, mimeType?: string | null): AttachmentKind | null {
  const type = String(messageType || '').toLowerCase();
  if (type === 'image' || type === 'video' || type === 'audio' || type === 'document') return type;
  if (type === 'file') {
    const mime = String(mimeType || '').toLowerCase();
    if (mime.startsWith('video/')) return 'video';
    if (mime.startsWith('audio/')) return 'audio';
    if (mime.startsWith('image/')) return 'image';
    return 'document';
  }
  return null;
}
